import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { COLORS, axis, box, Wrap } from "./DashCharts";
import { Empty } from "./BiUi";
import { attendanceStatusOf } from "./auditExports";

const HIERARCHY = { Nursery: 1, LKG: 2, HKG: 3, "1st": 10, "2nd": 20, "3rd": 30, "4th": 40, "5th": 50, "6th": 60, "7th": 70, "8th": 80, "9th": 90, "10th": 100, "11th": 110, "12th": 120 };
const rank = (n) => { const k = Object.keys(HIERARCHY).find((h) => String(n || "").startsWith(h)); return k ? HIERARCHY[k] : 500; };
export const sortByClass = (a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b), undefined, { numeric: true });

const fmtTime = (t) => (t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "");
const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });

const attStatus = (r) => {
  const s = attendanceStatusOf(r);
  const cls = s.key === "Pending" ? "text-rose-600" : s.key === "Late" ? "text-amber-600" : "text-emerald-600";
  return { icon: s.icon, text: s.label, cls };
};

/** Today's live tracker: class, teacher, attendance (time + punctuality), diary. */
export function ComplianceGrid({ rows }) {
  const list = [...(rows || [])].sort((a, b) => sortByClass(a.classId, b.classId));
  if (!list.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <th className="py-3 pr-4">Class / Teacher</th>
            <th className="py-3 pr-4">Attendance</th>
            <th className="py-3">Daily Diary</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {list.map((r) => {
            const a = attStatus(r);
            return (
              <tr key={r.classId} className="hover:bg-slate-50">
                <td className="py-3 pr-4" title={`Class ${r.classId} | ${r.teacherName || "No teacher assigned"}`}>
                  <div className="font-bold text-slate-900">{r.classId}</div>
                  <div className="text-[10px] font-medium text-slate-500">{r.teacherName || "Unassigned"}</div>
                </td>
                <td className={`py-3 pr-4 font-bold ${a.cls}`} title={r.attendanceDone ? `Submitted at ${fmtTime(r.attendanceSubmittedAt || r.submittedAt)} by ${r.teacherName || "teacher"}` : "Not submitted yet"}>
                  {a.icon} {a.text}
                </td>
                <td className={`py-3 font-bold ${r.diaryDone ? "text-emerald-600" : "text-slate-400"}`} title={r.diaryDone ? `Submitted at ${fmtTime(r.diarySubmittedAt || r.submittedAt)}` : "Diary not submitted"}>
                  {r.diaryDone ? `🟢 Submitted${r.diarySubmittedAt ? ` (${fmtTime(r.diarySubmittedAt)})` : ""}` : "⚪ Pending"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_COL = { "On-Time": COLORS.present, Late: COLORS.leave, Missing: COLORS.absent, Pending: "#cbd5e1", Holiday: "#a5b4fc" };
const rateCls = (p) => (p >= 75 ? "text-emerald-600" : p >= 50 ? "text-amber-600" : "text-rose-600");

/** 7 working-day strip per teacher. Green = on time, Amber = late, Red = skipped, grey = today pending. */
export function ComplianceHeatmap({ matrix }) {
  if (!matrix || !matrix.length) return <Empty />;
  const rows = [...matrix].sort((a, b) => sortByClass(a.classId, b.classId));
  const tip = (m, s) => `${m.teacherName} (Class ${m.classId})\n${fmtDay(s.date)}: ${s.status}`
    + (s.attendanceSubmittedAt ? `\nAttendance: ${fmtTime(s.attendanceSubmittedAt)}` : "")
    + (s.diarySubmittedAt ? `\nDiary: ${fmtTime(s.diarySubmittedAt)}` : s.diaryDone ? "\nDiary: submitted" : "\nDiary: pending");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-400">
            <th className="py-3 pr-4">Teacher / Class</th>
            <th className="py-3">Last 7 Working Days</th>
            <th className="py-3 pl-3 text-right" title="Days attendance was marked / working days">Attendance</th>
            <th className="py-3 pl-3 text-right" title="Days diary topic/homework assigned / working days">Diary &amp; HW</th>
            <th className="py-3 pl-3 text-right" title="Submissions before cutoff / submissions">Punctuality</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((m) => (
            <tr key={m.classId} className="hover:bg-slate-50">
              <td className="py-3 pr-4">
                <div className="font-bold text-slate-900">{m.teacherName}</div>
                <div className="text-[10px] font-medium text-slate-500">Class {m.classId}</div>
              </td>
              <td className="py-3">
                <div className="flex gap-1.5">
                  {m.stats.map((s) => (
                    <div key={s.date} title={tip(m, s)}
                      className="h-5 w-5 rounded-md shadow-sm transition-transform hover:scale-110"
                      style={{ backgroundColor: STATUS_COL[s.status] || "#f1f5f9" }} />
                  ))}
                </div>
              </td>
              <td className={`py-3 pl-3 text-right font-black ${rateCls(m.attendanceRate)}`}>{m.attendanceRate}%</td>
              <td className={`py-3 pl-3 text-right font-black ${rateCls(m.diaryRate)}`}>{m.diaryRate}%</td>
              <td className={`py-3 pl-3 text-right font-black ${rateCls(m.punctualityRate)}`}>{m.punctualityRate}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex flex-wrap gap-4 text-[10px] font-bold uppercase text-slate-400">
        {[["On-Time", "On-Time"], ["Holiday", "Holiday (not penalised)"], ["Late", "Late"], ["Missing", "Skipped"], ["Pending", "Today (pending)"]].map(([k, l]) => (
          <span key={k} className="flex items-center gap-1.5"><b className="h-2 w-2 rounded-full" style={{ background: STATUS_COL[k] }} />{l}</span>
        ))}
      </div>
    </div>
  );
}

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short" });
const barTip = ({ active, payload }) => (active && payload && payload[0] ? (
  <div style={box}>
    <b>{monthLabel(payload[0].payload.month)} {payload[0].payload.month.slice(0, 4)}</b>
    {payload.map((p) => <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}%</div>)}
    <div className="text-slate-500">{payload[0].payload.workdays} working days so far</div>
  </div>
) : null);

/** 12-month academic-year compliance: attendance, diary and on-time % per month. */
export function ComplianceLongitudinal({ report }) {
  if (!report || !report.length) return <Empty />;
  const data = report.map((r) => ({ ...r, label: monthLabel(r.month) }));
  return (
    <Wrap>
      <BarChart data={data} margin={{ top: 8, right: 16, left: -10, bottom: 4 }}>
        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" {...axis} />
        <YAxis domain={[0, 100]} unit="%" {...axis} />
        <Tooltip cursor={{ fill: "#f1f5f9" }} content={barTip} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="attendancePct" name="Attendance" fill={COLORS.present} radius={[3, 3, 0, 0]} />
        <Bar dataKey="diaryPct" name="Diary" fill={COLORS.academic} radius={[3, 3, 0, 0]} />
        <Bar dataKey="punctualityPct" name="On-time" fill={COLORS.leave} radius={[3, 3, 0, 0]} />
      </BarChart>
    </Wrap>
  );
}
